import { BEARDS, HAIR_COLORS, HAIR_STYLES, SKIN_TONES, encodeFace, faceOf, type FaceSpec } from '@shared/types';
import { Portrait } from '../Portrait';

/**
 * Elegir el rostro del jugador (lobby «Tu jugador» y Ajustes): tono de piel,
 * cabello, color de cabello y barba, con vista previa del retrato. «Otra cara»
 * cambia la forma de la cara, ojos, cejas y boca. Se guarda como el país.
 */
export const FacePicker = ({
  value,
  seed,
  onChange,
  color,
  color2,
  compact = false,
  disabled = false,
}: {
  /** Código del rostro elegido (null = automático). */
  value: string | null | undefined;
  /** Id del jugador (rostro automático estable). */
  seed: string;
  onChange: (code: string | null) => void;
  color: string;
  color2?: string;
  compact?: boolean;
  disabled?: boolean;
}) => {
  const face = faceOf(value, seed);
  const set = (patch: Partial<FaceSpec>) => onChange(encodeFace({ ...face, ...patch }));
  const chip = 'grid h-7 min-w-7 place-items-center rounded-md border text-[10px] font-semibold transition';
  const on = 'border-accent-400 ring-2 ring-accent-400/60';
  const off = 'border-white/10 hover:border-white/30';
  return (
    <div className={`flex gap-3 ${compact ? 'flex-col items-center' : 'items-start'}`} data-testid="face-picker">
      <div className="shrink-0 overflow-hidden rounded-full border-2 border-white/15 shadow-lg">
        <Portrait face={face} color={color} color2={color2} size={compact ? 72 : 88} title="Vista previa de tu rostro" />
      </div>
      <div className="min-w-0 flex-1 space-y-2 text-[11px] text-slate-300">
        <div>
          <div className="mb-1 font-semibold text-slate-400">Tono de piel</div>
          <div role="radiogroup" aria-label="Tono de piel" className="flex flex-wrap gap-1">
            {SKIN_TONES.map((c, i) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={face.skin === i}
                aria-label={`Tono ${i + 1}`}
                disabled={disabled}
                onClick={() => set({ skin: i })}
                className={`${chip} ${face.skin === i ? on : off}`}
                style={{ background: c }}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1 font-semibold text-slate-400">Cabello</div>
          <div role="radiogroup" aria-label="Cabello" className="flex flex-wrap gap-1">
            {HAIR_STYLES.map((name, i) => (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={face.hair === i}
                disabled={disabled}
                onClick={() => set({ hair: i })}
                className={`${chip} px-1.5 ${face.hair === i ? `${on} bg-accent-500/20 text-white` : `${off} bg-white/5`}`}
              >
                {name}
              </button>
            ))}
          </div>
          <div role="radiogroup" aria-label="Color de cabello" className="mt-1 flex flex-wrap gap-1">
            {HAIR_COLORS.map((c, i) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={face.hairColor === i}
                aria-label={`Color de cabello ${i + 1}`}
                disabled={disabled}
                onClick={() => set({ hairColor: i })}
                className={`${chip} ${face.hairColor === i ? on : off}`}
                style={{ background: c }}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1 font-semibold text-slate-400">Barba</div>
          <div role="radiogroup" aria-label="Barba" className="flex flex-wrap gap-1">
            {BEARDS.map((name, i) => (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={face.beard === i}
                disabled={disabled}
                onClick={() => set({ beard: i })}
                className={`${chip} px-1.5 ${face.beard === i ? `${on} bg-accent-500/20 text-white` : `${off} bg-white/5`}`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={disabled}
            onClick={() => set({ shape: (face.shape + 1) % 4, eyes: (face.eyes + 3) % 4, brows: (face.brows + 1) % 4, mouth: (face.mouth + 1) % 4 })}
            className="btn-ghost h-7 px-2 text-[11px]"
          >
            🎲 Otra cara
          </button>
          {value && (
            <button type="button" disabled={disabled} onClick={() => onChange(null)} className="btn-ghost h-7 px-2 text-[11px]">
              Automático
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
